import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from '@uidotdev/usehooks';

import { allTypeProjectFilter, projectFilterAtom, useTodoTasksContext } from '../data-access';
import { ProjectFilterItem } from './types';
import { useRecoilState } from 'recoil';
import { trackEventMixpanel } from '../utils';
import { getDefaultBoard } from '../helpers';

export const useProjectFilter = () => {
  const { todoProjects } = useTodoTasksContext();
  const [selectedProjectFilters, setSelectedProjectFilters] = useRecoilState(projectFilterAtom);
  const [lastUsedBoardId] = useLocalStorage<string>('lifestack_last_used_board_id', '');

  const { filterProjectOptions, projectOptionsWithoutAll } = useMemo(() => {
    const projectOptionsWithoutAll = todoProjects
      .filter((p) => !p.closed)
      .map(
        (project) =>
          ({
            todoTaskId: project.todoTaskId,
            name: project.name,
            appFrom: project.appFrom,
          }) as ProjectFilterItem
      );
    return {
      filterProjectOptions: [allTypeProjectFilter, ...projectOptionsWithoutAll],
      projectOptionsWithoutAll,
    };
  }, [todoProjects]);

  const getDefaultProject = useCallback(() => {
    return getDefaultBoard({
      selectedFilters: selectedProjectFilters,
      filterProjectOptions,
      lastUsedBoardId,
    });
  }, [selectedProjectFilters, filterProjectOptions, lastUsedBoardId]);

  const handleSelectFilterProject = (projectId: string) => {
    trackEventMixpanel('select_project', { projectId });

    if (projectId === 'all') {
      setSelectedProjectFilters([allTypeProjectFilter]);
      return;
    }

    const project = filterProjectOptions.find(
      (p) => p.todoTaskId === projectId
    ) as typeof allTypeProjectFilter;

    const currentFilters = selectedProjectFilters.filter((p) => p.todoTaskId !== 'all');
    const isProjectSelected = currentFilters.some((p) => p.todoTaskId === projectId);

    let newSelectedFilters = isProjectSelected
      ? currentFilters.filter((p) => p.todoTaskId !== projectId)
      : [...currentFilters, project];

    if (newSelectedFilters.length === 0) {
      newSelectedFilters = [allTypeProjectFilter];
    }

    setSelectedProjectFilters(newSelectedFilters);
  };

  return {
    selectedFilters: selectedProjectFilters,
    filterProjectOptions,
    projectOptionsWithoutAll,
    getDefaultProject,
    handleSelectFilterProject,
  };
};
